import { BlogPostRegistry, BlogPostMetadata, BlogPost, BlogPostModule } from './types';
import { blogPostsMetadata, blogMetadataBySlug } from './blog-metadata.generated';
import { maybeReloadOnceForStaleChunk } from '../utils/staleChunkRecovery';

// Dynamic imports for post content only (one chunk per post view)
export const blogPostRegistry: BlogPostRegistry = {
  'audio-memories-memory-workspace': () =>
    import('./posts/audio-memories-memory-workspace'),
  'memara-whats-new-2026': () => import('./posts/memara-whats-new-2026'),
  'memara-python-sdk-launch': () => import('./posts/memara-python-sdk-launch'),
  'zapier-integration-launch': () => import('./posts/zapier-integration-launch'),
  'chrome-extension-demo-walkthrough': () => import('./posts/chrome-extension-demo-walkthrough'),
  'chrome-extension-integration-launch': () => import('./posts/chrome-extension-integration-launch'),
  'x-bookmarks-integration-launch': () => import('./posts/x-bookmarks-integration-launch'),
  'memara-chrome-extension-spaces-teams': () =>
    import('./posts/memara-chrome-extension-spaces-teams'),
  'chatgpt-custom-gpt-memara-setup': () =>
    import('./posts/chatgpt-custom-gpt-memara-setup'),
  'what-is-memara': () => import('./posts/what-is-memara'),
  'how-to-connect-memara-to-other-apps': () =>
    import('./posts/how-to-connect-memara-to-other-apps'),
  'how-to-clear-simple-memory-n8n': () => import('./posts/how-to-clear-simple-memory-n8n'),
  'persistent-ai-memory-guide': () => import('./posts/persistent-ai-memory-guide'),
  'memara-vs-mem0-n8n-memory': () => import('./posts/memara-vs-mem0-n8n-memory'),
  'memara-featured-theaibreak-newsletter': () => import('./posts/memara-featured-theaibreak-newsletter'),
  'n8n-node-verified-cloud-launch': () => import('./posts/n8n-node-verified-cloud-launch'),
  'n8n-memara-polling-triggers': () => import('./posts/n8n-memara-polling-triggers'),
  'memara-n8n-integration-announced': () => import('./posts/memara-n8n-integration-announced'),
  'memara-mcp-server-featured-claude-community': () => import('./posts/memara-mcp-server-featured-claude-community'),
  'building-persistent-memory-ai-agents': () => import('./posts/building-persistent-memory-ai-agents'),
  'graph-memory-vs-semantic-search-agents': () =>
    import('./posts/graph-memory-vs-semantic-search-agents'),
  'mastra-vs-mem0-memory-layer': () => import('./posts/mastra-vs-mem0-memory-layer'),
  'memory-for-multi-agent-systems': () => import('./posts/memory-for-multi-agent-systems'),
  'why-gpts-forget-how-to-fix': () => import('./posts/why-gpts-forget-how-to-fix'),
  'vector-search-vs-traditional-databases': () => import('./posts/vector-search-vs-traditional-databases'),
  'memory-spaces-organizing-ai-context': () => import('./posts/memory-spaces-organizing-ai-context'),
  'vector-database-vs-traditional-database': () => import('./posts/vector-database-vs-traditional-database'),
  'memara-vs-n8n-simple-memory': () => import('./posts/memara-vs-n8n-simple-memory'),
  'what-is-simple-memory-in-n8n': () => import('./posts/what-is-simple-memory-in-n8n'),
  'n8n-simple-memory-node-guide': () => import('./posts/n8n-simple-memory-node-guide'),
  'n8n-workflow-memory-from-basic-to-intelligent': () => import('./posts/n8n-workflow-memory-from-basic-to-intelligent'),
  'why-our-logo-is-clarks-nutcracker': () => import('./posts/why-our-logo-is-clarks-nutcracker'),
};

const postCache = new Map<string, BlogPost>();

function sortPostsByDate(posts: BlogPostMetadata[]): BlogPostMetadata[] {
  return [...posts].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

function publishedPosts(): BlogPostMetadata[] {
  return blogPostsMetadata.filter((post) => post.published !== false);
}

/**
 * Get all blog post metadata (static manifest; no dynamic imports)
 */
export async function getAllPostsMetadata(): Promise<BlogPostMetadata[]> {
  return sortPostsByDate(publishedPosts());
}

/**
 * Get a specific blog post by slug (loads content chunk only)
 */
export async function getPostBySlug(slug: string): Promise<BlogPost | null> {
  if (postCache.has(slug)) {
    return postCache.get(slug)!;
  }

  const loader = blogPostRegistry[slug];
  if (!loader) {
    return null;
  }

  try {
    const module = await loader();
    const blogPostModule: BlogPostModule =
      (module as { default?: BlogPostModule }).default ?? (module as BlogPostModule);

    if (blogPostModule.metadata && blogPostModule.content) {
      const post: BlogPost = {
        ...blogPostModule.metadata,
        content: blogPostModule.content,
      };

      postCache.set(slug, post);
      return post;
    }

    return null;
  } catch (error) {
    maybeReloadOnceForStaleChunk(error);
    console.error(`Failed to load post: ${slug}`, error);
    return null;
  }
}

/**
 * Get featured posts
 */
export async function getFeaturedPosts(limit?: number): Promise<BlogPostMetadata[]> {
  const featured = publishedPosts().filter((post) => post.featured);
  const sorted = sortPostsByDate(featured);
  return limit ? sorted.slice(0, limit) : sorted;
}

/**
 * Get posts by tag
 */
export async function getPostsByTag(tag: string, limit?: number): Promise<BlogPostMetadata[]> {
  const taggedPosts = publishedPosts().filter((post) =>
    post.tags.some((t) => t.toLowerCase() === tag.toLowerCase())
  );
  const sorted = sortPostsByDate(taggedPosts);
  return limit ? sorted.slice(0, limit) : sorted;
}

/**
 * Get posts by author
 */
export async function getPostsByAuthor(author: string, limit?: number): Promise<BlogPostMetadata[]> {
  const authorPosts = publishedPosts().filter(
    (post) => post.author.toLowerCase() === author.toLowerCase()
  );
  const sorted = sortPostsByDate(authorPosts);
  return limit ? sorted.slice(0, limit) : sorted;
}

/**
 * Search posts by content (simple text search on metadata)
 */
export async function searchPosts(query: string, limit?: number): Promise<BlogPostMetadata[]> {
  const searchTerm = query.toLowerCase();

  const matches = publishedPosts().filter((post) => {
    return (
      post.title.toLowerCase().includes(searchTerm) ||
      post.excerpt.toLowerCase().includes(searchTerm) ||
      post.tags.some((tag) => tag.toLowerCase().includes(searchTerm)) ||
      (post.seoKeywords &&
        post.seoKeywords.some((keyword) => keyword.toLowerCase().includes(searchTerm)))
    );
  });

  const sorted = sortPostsByDate(matches);
  return limit ? sorted.slice(0, limit) : sorted;
}

/**
 * Get all unique tags
 */
export async function getAllTags(): Promise<{ tag: string; count: number }[]> {
  const tagCounts = new Map<string, number>();

  publishedPosts().forEach((post) => {
    post.tags.forEach((tag) => {
      tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
    });
  });

  return Array.from(tagCounts.entries())
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count);
}

/**
 * Get related posts based on tags (metadata only)
 */
export async function getRelatedPosts(
  currentSlug: string,
  limit: number = 3
): Promise<BlogPostMetadata[]> {
  const currentPost = blogMetadataBySlug[currentSlug];
  if (!currentPost) return [];

  const otherPosts = publishedPosts().filter((post) => post.slug !== currentSlug);

  const scoredPosts = otherPosts.map((post) => {
    const commonTags = post.tags.filter((tag) => currentPost.tags.includes(tag)).length;
    return { post, score: commonTags };
  });

  return scoredPosts
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((item) => item.post);
}

/**
 * Clear the content cache (useful for development)
 */
export function clearCache(): void {
  postCache.clear();
}

/**
 * Preload critical posts (for performance)
 */
export async function preloadCriticalPosts(): Promise<void> {
  const featuredPosts = await getFeaturedPosts(2);

  await Promise.allSettled(featuredPosts.map((post) => getPostBySlug(post.slug)));
}
